import { SimilarPartsTab } from '@hub/bearing-hub/ProductDetails/ProductTabs/SimilarPartsTab';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { KoplaTeaser } from '@hub/common/KoplaTeaser/KoplaTeaser';
import type { TechnicalDataCategory } from '@hub/common/ProductTabs/tabs/SuitableProductsTab';
import { SuitableProductsTab } from '@hub/common/ProductTabs/tabs/SuitableProductsTab';
import { Section } from '@hub/common/Section/Section';
import { TechnicalTable } from '@hub/common/TechnicalTable/TechnicalTable';
import type { Teasers } from '@hub/generated/graphql';
import { useBreakpoints } from '@hub/hooks/useBreakpoints';
import { getLocale, IMPERIAL_UNIT_SYSTEM_LOCALES } from '@hub/libs/constants';
import { createMarkup } from '@hub/libs/utils';
import { Collapsible } from '@hub/common/Collapsible/Collapsible';
import { Tabs } from '@hub/common/Tabs/Tabs';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import type { ReactNode } from 'react';
import styles from './ProductDetailsTabs.module.css';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { IbhEventCategory } from '@hub/bearing-hub/common/tracking.config';
import { TRACKED_STANDARD_EVENT_ACTIONS } from '@hub/libs/tracking';
import type { SuitableProductProps } from '@hub/common/SuitableProduct/SuitableProduct';
import type {
  IbhCatalogArticleResponseDto,
  IbhCustomArticleResponseDto,
  IbhPartsResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhProductionMethods, IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { useDownloadTab } from '../../common/downloadTab/useDownloadTab';
import { getTechnicalDataCategoriesWithUnits } from './technical-data.factory';
import { FallbackProductDescription } from '@hub/common/FallbackProductDescription/FallbackProductDescription';
import { useRouter } from 'next/router';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { UnitSystem } from '@igus/kopla-domain';

const DownloadTab = dynamic(
  () => import('@hub/bearing-hub/common/downloadTab/DownloadTab').then(mod => mod.DownloadTab),
  {
    ssr: false,
  }
);

export interface ProductDetailsTabsProps {
  articleData: IbhCustomArticleResponseDto | IbhCatalogArticleResponseDto;
  productDetails: string;
  technicalDataCategories: TechnicalDataCategory[];
  suitableProducts: SuitableProductProps[];
  selectedShape: ShapeDescriptor;
  onlineTools: Teasers[];
  similarParts: IbhPartsResponseDto[];
  isCustom: boolean;
  dimensions: IbhDimensions;
  quantity: number;
  unitSystem: UnitSystem;
  session: Session;
}

export const ProductDetailsTabs = ({
  articleData,
  productDetails,
  technicalDataCategories,
  suitableProducts,
  selectedShape,
  onlineTools,
  similarParts,
  isCustom,
  dimensions,
  quantity,
  unitSystem,
  session,
}: ProductDetailsTabsProps) => {
  const { t } = useTranslation(['common', 'product/productDetails']);
  const { md } = useBreakpoints();
  const router = useRouter();
  const locale = getLocale(router);
  const ComponentToUse = md ? Tabs : Collapsible;
  const { shape, productionMethod, material, widens } = articleData;
  const { widenShortKeySchema, shapeKey } = selectedShape;
  const { CustomEvent } = usePiwikPro();
  const { downloadTabDefinition } = useDownloadTab(widens);
  const filteredOnlineTabs = onlineTools.filter(tool => tool?.id !== 'iglidur_gleitlagerdesigner');
  const trackTabSelect = (tabName: string) => {
    CustomEvent.trackEvent(IbhEventCategory, TRACKED_STANDARD_EVENT_ACTIONS.tabSelect, tabName);
  };

  const toleranceInfo = () => {
    const supportedMethods = [IbhProductionMethods.MOLDINJECTION, IbhProductionMethods.FASTLINE];
    const supportedShapes = [IbhShape.S, IbhShape.F];
    return (
      supportedShapes.includes(shapeKey) &&
      supportedMethods.includes(productionMethod) &&
      (!IMPERIAL_UNIT_SYSTEM_LOCALES.includes(locale) || unitSystem === UnitSystem.imperial)
    );
  };

  const shapeDrawing = (
    <div className="mt-8">
      <WidenImage
        shortCode={widenShortKeySchema}
        title={t(`SHAPES.${shapeKey}.TITLE`, { ns: 'bearing-hub/bearingHub' })}
        filename={widenShortKeySchema}
        width={281}
        height={145}
      />
    </div>
  );

  const tabItem = [
    {
      title: t(`PRODUCT_DETAILS.DESCRIPTION_TITLE`, { ns: 'product/productDetails' }),
      content: productDetails ? (
        <div className={styles.productDescriptionTab}>
          {/* eslint-disable-next-line react/no-danger */}
          <div dangerouslySetInnerHTML={createMarkup(productDetails)} />
        </div>
      ) : (
        <FallbackProductDescription materialName={material.id} productionMethod={productionMethod} />
      ),
    },
    {
      title: t(`PRODUCT_DETAILS.TECHNICAL_DATA`, { ns: 'product/productDetails' }),
      content: (
        <>
          {shapeDrawing}
          {articleData.productionMethod === IbhProductionMethods.SEMIFINISHED && (
            <p>{t(`PRODUCT_DETAILS.PRESS_FIT_INFORMATION`, { ns: 'product/productDetails' })}</p>
          )}
          {getTechnicalDataCategoriesWithUnits(technicalDataCategories, isCustom).map(category =>
            category.attributes.length > 0 ? (
              <Section key={category.name} title={t(category.label ?? '', { ns: 'product/productDetails' })}>
                <TechnicalTable data={category.attributes} />
                {category.name === 'manufacturing_and_installation_tolerances' && toleranceInfo() && (
                  <span className="inline-block pl-4 md:pl-[65px] lg:pl-[58px] xl:pl-[69px] mt-6">
                    {t('TECHNICAL_DATA_LABELS.TOLERANCE_INFO', { ns: 'bearing-hub/bearingHub' })}
                  </span>
                )}
              </Section>
            ) : null
          )}
        </>
      ),
    },
    suitableProducts.length > 0
      ? {
          title: t('PRODUCT_DETAILS.SUITABLE_PRODUCTS_TITLE', { ns: 'product/productDetails' }),
          content: <SuitableProductsTab suitableProducts={suitableProducts} />,
        }
      : null,
    filteredOnlineTabs.length > 0
      ? {
          title: t('PRODUCT_DETAILS.ONLINE_TOOLS_TITLE', { ns: 'product/productDetails' }),
          content: <KoplaTeaser tools={filteredOnlineTabs} />,
        }
      : null,
    {
      title: t('PRODUCT_DETAILS.DOWNLOADS', { ns: 'product/productDetails' }),
      content: session ? (
        <DownloadTab
          downloadTabDefinition={downloadTabDefinition}
          articleNumber={articleData.articleNumber}
          shape={shape}
          isCustom={isCustom}
          dimensions={dimensions}
          quantity={quantity}
          session={session}
          unitSystem={articleData.unitSystem as unknown as UnitSystem}
          productionMethod={articleData.productionMethod.replace(/_/g, '-') as IbhProductionMethods}
        />
      ) : null,
    },
    similarParts.length > 0
      ? {
          title: t('COMMON.SIMILAR_PARTS.TAB_TITLE', { ns: 'bearing-hub/bearingHub', count: similarParts?.length }),
          content: (
            <SimilarPartsTab
              similarParts={similarParts}
              selectedShape={selectedShape}
              articleData={articleData}
              unitSystem={unitSystem}
            />
          ),
        }
      : null,
  ].filter(item => !!item && (typeof item.content === 'string' ? item.content !== '' : true)) as Array<{
    title: string;
    content: ReactNode;
  }>;

  return (
    <div className={styles.tabsContainer}>
      <ComponentToUse items={tabItem} onTabButtonClick={index => trackTabSelect(tabItem[index].title)} />
    </div>
  );
};
